import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  Checkbox,
  Tabs,
  Tab,
  Toolbar,
  FormGroup,
  FormControlLabel,
  Typography,
} from '@mui/material';

import SurveySection from './SurveySection';
import Prompt from '../layout/Prompt';

import {
  setSamplePayload,
  setContextQuestionAnswer,
  setDiscardSubmissionSection,
  deleteSectionSubmissions,
  createEmptySubmissionSections,
} from '../../redux/actions';
import {
  ExtendedSurvey,
  MainSubmissionAnswerPayload,
  SubmissionPayload,
  SubmissionSectionContext,
} from '../../redux/types';
import TrialOpenQuestion from '../trial/TrialOpenQuestion';

type State = {
  currentSection: number;
  showDiscardSectionPrompt: boolean;
}

class Survey extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentSection: 0,
      showDiscardSectionPrompt: false,
    };
  }

  componentDidMount() {
    const {
      surveyPayload,
      submissionPayload,
      createEmptySubmissionSections: createEmptySubmissionSectionsAction,
    } = this.props;
    if (submissionPayload.sections.length !== surveyPayload.sections.length) {
      createEmptySubmissionSectionsAction(surveyPayload.sections.length);
    }
  }

  getSubmissionAnswer = (searchQuestionUuid: string) => {
    const { submissionPayload } = this.props;
    if (!submissionPayload) {
      return undefined; // we can not judge yet
    }
    const found = submissionPayload.sections.find((section) => {
      // section might be 'null' in case we started filling out beyond the first second
      const key = section && Object.keys(section.questions)
        .find((questionUuid) => questionUuid === searchQuestionUuid);
      return key && section.questions[key];
    });
    return found && found.questions[searchQuestionUuid].answers;
  };

  onParticipaionOptoutCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checkStatus = event.target.checked;
    const { currentSection: currentSectionState } = this.state;
    const {
      setDiscardSubmissionSection: setDiscardSubmissionSectionAction,
    } = this.props;
    if (checkStatus) {
      this.setState({ showDiscardSectionPrompt: true });
    } else {
      setDiscardSubmissionSectionAction(currentSectionState, false);
    }
  };

  onConfirmParticipationOptOut = (action: boolean) => {
    if (action) {
      const {
        setDiscardSubmissionSection: setDiscardSubmissionSectionAction,
        deleteSectionSubmissions: deleteSectionSubmissionsAction,
      } = this.props;
      const { currentSection: currentSectionState } = this.state;
      setDiscardSubmissionSectionAction(currentSectionState, true);
      deleteSectionSubmissionsAction(currentSectionState);
    }
    this.setState({ showDiscardSectionPrompt: false });
  };

  renderSections = (
    sections: ExtendedSection[],
    submissionPayload: SubmissionPayload,
    dispatchSamplePayload: typeof setSamplePayload,
    dispatchContextQuestionAnswer: typeof setContextQuestionAnswer,
  ) => sections.map((section, sectionIndex) => {
    const key = `${sectionIndex}-${section.headline}`;
    return (
      <SurveySection
        key={key}
        payload={section}
        sectionSubmission={
          submissionPayload.sections
          && submissionPayload.sections[sectionIndex]
        }
        startCase={
          sections
            .slice(0, sectionIndex)
            .reduce((p, c) => (p + c.numCases), 0)
        }
        onSamplePayloadChanged={(
          questionUuid: string, sampleIndex: number, payload: MainSubmissionAnswerPayload,
        ) => {
          dispatchSamplePayload(sectionIndex, questionUuid, sampleIndex, payload);
        }}
        getSubmissionAnswer={this.getSubmissionAnswer}
        onContextQuestionAnswer={(questionUuid: string, answers: SubmissionSectionContext['uuid']) => (
          dispatchContextQuestionAnswer(
            sectionIndex, questionUuid, answers,
          )
        )}
      />
    );
  });

  renderSectionsAsTabs = (
    sections: ExtendedSection[],
    submissionPayload: SubmissionPayload,
    dispatchSamplePayload: typeof setSamplePayload,
    dispatchContextQuestionAnswer: typeof setContextQuestionAnswer,
  ) => {
    const {
      currentSection,
      onCurrentSectionChange,
      optionalSections,
    } = this.props;
    const { currentSection: currentSectionState } = this.state;
    const selectedTabStyle = {
      backgroundColor: '#66a3e5',
      color: 'white',
      borderBottom: '1px solid red',
    };
    const unselectedTabStyle = {
      backgroundColor: 'lightgray',
      color: 'black',
      border: 'solid 2px white',
    };
    const sectionSelected = currentSection !== undefined
      ? currentSection : currentSectionState;
    return (
      <>
        <Tabs
          value={sectionSelected}
          indicatorColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {
            sections.map((section, sectionIndex) => {
              const key = `${sectionIndex}-${section.headline}`;
              return (
                <Tab
                  key={key}
                  label={section.headline || `Section ${sectionIndex}`}
                  onClick={() => {
                    if (onCurrentSectionChange) {
                      onCurrentSectionChange(sectionIndex);
                    }

                    this.setState({ currentSection: sectionIndex });
                  }}
                  value={sectionIndex}
                />
              );
            })
          }
        </Tabs>
        {
          optionalSections
          && submissionPayload.sections?.length > 0
          && (
            <Toolbar variant="dense">
              <span style={{ flexGrow: 1 }} />
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      onChange={this.onParticipaionOptoutCheck}
                      checked={
                        submissionPayload.sections[sectionSelected]
                          ? submissionPayload.sections[sectionSelected].discardSubmission
                          : false
                      }
                    />
                  )}
                  label={<Typography><FormattedMessage id="survey.participateOptOut" /></Typography>}
                />
              </FormGroup>
            </Toolbar>
          )
        }
        <SurveySection
          key={`SurveySection-${sectionSelected}`}
          payload={sections[sectionSelected]}
          sectionSubmission={
            submissionPayload.sections
            && submissionPayload.sections[sectionSelected]
          }
          startCase={
            sections
              .slice(0, sectionSelected)
              .reduce((p, c) => (p + c.numCases), 0)
          }
          onSamplePayloadChanged={(
            questionUuid: string, sampleIndex: number, payload: MainSubmissionAnswerPayload,
          ) => {
            dispatchSamplePayload(sectionSelected, questionUuid, sampleIndex, payload);
          }}
          getSubmissionAnswer={this.getSubmissionAnswer}
          onContextQuestionAnswer={(questionUuid: string, answers: SubmissionSectionContext['uuid']) => (
            dispatchContextQuestionAnswer(
              sectionSelected, questionUuid, answers,
            )
          )}
        />
      </>
    );
  };

  render() {
    const {
      surveyPayload,
      setSamplePayload: dispatchSamplePayload,
      setContextQuestionAnswer: dispatchContextQuestionAnswer,
      submissionPayload,
    } = this.props;
    const {
      showDiscardSectionPrompt,
    } = this.state;
    if (!surveyPayload) {
      return null;
    }

    return (
      <>
        <div>
          {
            surveyPayload.sections.some((section) => !!section.headline)
              ? this.renderSectionsAsTabs(
                surveyPayload.sections,
                submissionPayload,
                dispatchSamplePayload,
                dispatchContextQuestionAnswer,
              )
              : this.renderSections(
                surveyPayload.sections,
                submissionPayload,
                dispatchSamplePayload,
                dispatchContextQuestionAnswer,
              )
          }
          {
            surveyPayload.openQuestions
            && surveyPayload.openQuestions.map((openQuestion) => (
              <TrialOpenQuestion
                key={openQuestion.question.uuid}
                question={openQuestion}
                answers={
                  submissionPayload
                  && submissionPayload.openQuestionAnswers
                  && submissionPayload.openQuestionAnswers[openQuestion.question.uuid]
                }
                onChange={() => {
                  // passing empty method
                  // as openQuestions will be readonly and no longer supported
                }}
              />
            ))
          }
        </div>
        <Prompt
          display={showDiscardSectionPrompt}
          title={<FormattedMessage id="survey.participateOptOut" />}
          action={this.onConfirmParticipationOptOut}
        >
          <FormattedMessage id="survey.confirmOptOut" />
        </Prompt>
      </>
    );
  }
}

const dispatchProps = {
  setSamplePayload,
  setContextQuestionAnswer,
  setDiscardSubmissionSection,
  deleteSectionSubmissions,
  createEmptySubmissionSections,
};

const connector = connect(null, dispatchProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & {
  submissionPayload: SubmissionPayload;
  surveyPayload: (ExtendedSurvey)['payload'];
  onCurrentSectionChange?: (sectionId: number) => void;
  currentSection?: number;
  optionalSections: boolean;
};
type ExtendedSection = (ExtendedSurvey)['payload']['sections'][0];

export default connector(Survey);
